import { useEffect, useState } from 'react';
import type { RefObject } from 'react';

/**
 * Observe an element's width and re-render when it changes.
 *
 * Returns `null` until the first measurement so callers can distinguish
 * "not yet measured" from "measured at 0px".
 *
 * Falls back to a single getBoundingClientRect read in environments
 * without ResizeObserver (e.g. older test runners).
 */
export function useElementWidth<T extends HTMLElement>(
  ref: RefObject<T | null>,
): number | null {
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof ResizeObserver === 'undefined') {
      setWidth(el.getBoundingClientRect().width);
      return;
    }

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);

    return () => ro.disconnect();
  }, [ref]);

  return width;
}
